import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { getTzStartOfMonth, getTzEachDayOfInterval, getTzEndOfMonth, getTzStartOfWeek, getTzIsSameMonth, addDays, getTzEndOfWeek, formatDate, getTzIsSameDay, getTzTimeStates, getTzDate } from '@ninja/utilities';
import { useWebUIConfigOptions } from './ThemeProvider2.js';
import styles from './DatePicker4.js';
import { checkIsValidRangeForDaysView } from './utils6.js';
import { useCalendarViewContext } from './components10.js';

const DaysView = ({ onClick, value, secondaryValue, viewState, updateViewState, }) => {
    const { fromDate, toDate, enableTimeSelection, isIntervalPicker, maxSelectionRangeDays } = useCalendarViewContext();
    const { locale } = useWebUIConfigOptions();
    const { tzDate, currentCalendar } = viewState;
    const firstDayOfMonth = getTzStartOfMonth(tzDate, currentCalendar);
    const { intervalStartDate, intervalEndDate, intervalEndDateHover } = viewState;
    if (!firstDayOfMonth)
        return jsx(Fragment, {});
    const daysInMonthArray = getTzEachDayOfInterval({
        tzDateStart: firstDayOfMonth,
        tzDateEnd: getTzEndOfMonth(firstDayOfMonth, currentCalendar),
    }).map((date) => ({ date, currentMonth: true }));
    const startOfFirstWeek = getTzStartOfWeek(daysInMonthArray[0].date);
    const previousMonthDays = getTzIsSameMonth(startOfFirstWeek, daysInMonthArray[0].date, currentCalendar)
        ? []
        : getTzEachDayOfInterval({
            tzDateStart: startOfFirstWeek,
            tzDateEnd: addDays(daysInMonthArray[0].date, -1),
        }).map((date) => ({ date, currentMonth: false }));
    const endOfLastWeek = getTzEndOfWeek(daysInMonthArray[daysInMonthArray.length - 1].date);
    const nextMonthDays = getTzIsSameMonth(endOfLastWeek, daysInMonthArray[daysInMonthArray.length - 1].date, currentCalendar)
        ? []
        : getTzEachDayOfInterval({
            tzDateStart: addDays(daysInMonthArray[daysInMonthArray.length - 1].date, 1),
            tzDateEnd: endOfLastWeek,
        }).map((date) => ({ date, currentMonth: false }));
    const daysToShow = [...previousMonthDays, ...daysInMonthArray, ...nextMonthDays];
    return (jsxs("div", { className: styles.dayViewContainer, children: [jsx("div", { className: styles.dayViewDaysNamesContainer, children: daysToShow
                    .filter((a, index) => index < 7)
                    .map((day, idx) => {
                    return (jsx("div", { className: styles.dayViewDayNameItem, children: formatDate(day.date, {
                            dateFormat: locale === 'ar' ? 'EEEEE' : 'EEEEEE',
                            calendar: currentCalendar,
                        }) }, idx));
                }) }), jsx("div", { className: styles.daysViewGrid, children: daysToShow.map((day, idx) => {
                    const isValidHover = !!intervalEndDateHover &&
                        !!intervalStartDate &&
                        intervalEndDateHover > intervalStartDate &&
                        day.date > intervalStartDate &&
                        day.date <= intervalEndDateHover;
                    const isSelected = !!value && getTzIsSameDay(value, day.date);
                    const isSecondarySelected = (!!secondaryValue && getTzIsSameDay(secondaryValue, day.date)) ||
                        (isValidHover && intervalEndDateHover <= day.date);
                    const isInBetween = (!!value && !!secondaryValue && day.date < secondaryValue && day.date > value) || isValidHover;
                    const isFirstSelection = isSelected &&
                        !intervalEndDateHover &&
                        (!intervalEndDate || (intervalStartDate && getTzIsSameDay(intervalStartDate, intervalEndDate)));
                    const disabled = !checkIsValidRangeForDaysView({
                        isIntervalPicker,
                        maxSelectionRangeDays,
                        intervalStartDate,
                        intervalEndDate,
                        currentDate: value,
                        refDate: day.date,
                        fromDate,
                        toDate,
                    });
                    const getSelectionMode = () => {
                        if (isSelected)
                            return 'primary';
                        if (isSecondarySelected)
                            return 'secondary';
                        if (isInBetween || intervalEndDateHover)
                            return 'ternary';
                        return undefined;
                    };
                    return (jsx("div", { className: styles.dayItem(isSelected || isSecondarySelected || isInBetween, disabled, day.currentMonth, getSelectionMode(), isIntervalPicker, isFirstSelection), onClick: () => {
                            if (!disabled) {
                                let selectedDate = day.date;
                                if (enableTimeSelection) {
                                    const currentDateState = getTzTimeStates(day.date, currentCalendar);
                                    const { minutes, hours } = getTzTimeStates(tzDate, currentCalendar);
                                    selectedDate = getTzDate(Object.assign(Object.assign({}, currentDateState), { minutes, hours }));
                                }
                                onClick(selectedDate);
                            }
                        }, onMouseOver: () => {
                            if (isIntervalPicker && intervalStartDate && !intervalEndDate && day.date > intervalStartDate) {
                                updateViewState === null || updateViewState === void 0 ? void 0 : updateViewState({ intervalEndDateHover: day.date });
                            }
                            else
                                updateViewState === null || updateViewState === void 0 ? void 0 : updateViewState({ intervalEndDateHover: undefined });
                        }, children: formatDate(day.date, {
                            dateFormat: 'd',
                            calendar: currentCalendar,
                        }) }, idx));
                }) })] }));
};

export { DaysView };
